import { useEffect, useState } from "react";

import {
  DatePicker,
  TextField,
  Select,
  Checkbox,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getDifficultyTypeList,
  getRequestDocument,
  getForm7,
  saveForm7,
  getEnrollmentDecision,
  getRequest,
  getCommitteesForDocument,
} from "../../../api/Request/RequestApi";
import { useAppDispatch } from "../../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../../components";
import { useAuth } from "../../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../../models";
import {
  Request,
  RequestStatus,
  RequestType,
  DocumentType,
  Form7,
  EnrollmentDecision,
  DocumentCommittee,
} from "../../../models/Request";
import { Attachment } from "../../../models/Shared/Attachment";
import { requestAdminDetailsUrl } from "../../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../../slices/applicationSlice";
import { CommissionSelection } from "../components";
import { DEFAULT_FORM7, DEFAULT_REQUEST } from "../constants";
import { Form7TypeSection, Form7InstitutionSelect } from "./components";
import {
  createEarlyEnrollmentApprovedPayload,
  createTemporaryExemptionApprovedPayload,
} from "./components/Form7TypeSection";

export function Form7Edit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const auth = useAuth();

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [form7, setForm7] = useState<Form7>(DEFAULT_FORM7(+id));
  const [difficultyTypes, setDifficultyTypes] = useState<CodeBook[]>([]);
  const [enrollmentDecision, setEnrollmentDecision] = useState<
    EnrollmentDecision | undefined
  >();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [initialDocumentCommittees, setInitialDocumentCommittees] =
    useState<DocumentCommittee[]>();

  useEffect(() => {
    (async () => {
      const difficultyTypeList = await getDifficultyTypeList();
      if (difficultyTypeList) {
        setDifficultyTypes(difficultyTypeList);
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        const result = await getForm7(+id);

        if (result) {
          setForm7(result);
          setIsEditing(false);
          setInitialDocumentCommittees(result.committeesForDocument);
          setItem(result.request);
        } else {
          const request = await getRequest(+id);

          if (request) {
            setItem(() => request);
            switch (request.type) {
              case RequestType.EarlyEnrollment:
                setForm7((prevForm7) => {
                  const updatedForm7 = createEarlyEnrollmentApprovedPayload(
                    prevForm7,
                    request,
                    true
                  );

                  handleUpdate(updatedForm7);
                  return updatedForm7;
                });
                break;
              case RequestType.TemporaryExemption:
                setForm7((prevForm7) => {
                  const updatedForm7 = createTemporaryExemptionApprovedPayload(
                    prevForm7,
                    request,
                    true
                  );
                  handleUpdate(updatedForm7);
                  return updatedForm7;
                });
                break;
            }

            const committeesForDocument = await getCommitteesForDocument(
              +id,
              DocumentType.Form7
            );

            setInitialDocumentCommittees(committeesForDocument);
            setForm7((prevForm7) => ({
              ...prevForm7,
              request,
              committeesForDocument,
            }));
          }
        }

        const enrollmentDecisionItem = await getEnrollmentDecision(+id);
        if (enrollmentDecisionItem) {
          setEnrollmentDecision(enrollmentDecisionItem);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      item.institutionId &&
      form7.id === 0 &&
      item.type === RequestType.EarlyEnrollment
    ) {
      // There is a problem with the institutionId not being set on the form7
      // although it is shown in the InstitutionSelect. We have not been given
      // any default values for the different request types (for EarlyEnrollment,
      // is the default Approved or Rejected?). We will set an institution.
      // This may be updated based on the selections.
      const d: Form7 = {
        ...form7,
        institutionId: item.institutionId,
      };
      handleUpdate(d);
    }
  }, [item.institutionId]);

  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleSessionDateChanged = (val: Date) => {
    const d: Form7 = {
      ...form7,
      sessionDate: val,
    };
    handleUpdate(d);
  };
  const handleSessionPlaceChanged = (val?: string) => {
    const d: Form7 = {
      ...form7,
      sessionPlace: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleHasDifficultiesChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      hasDifficulties: val,
    };
    handleUpdate(d);
  };
  const handleDifficultyDescriptionChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      difficultyDescription: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleRulesChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      rules: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleNNNumberChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      nnNumber: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleSchoolingDescriptionChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      schoolingDescription: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleEducationalStatusChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      educationalStatus: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAppropriateHelpFormatChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      appropriateHelpFormat: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDifficultyTypeChange = (val?: number[]) => {
    const d: Form7 = {
      ...form7,
      difficultyTypes: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleOtherChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      other: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleInstitutionChange = (val?: number) => {
    const d: Form7 = {
      ...form7,
      institutionId: val && !isNaN(val) ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  const handleUpdate = (item: Form7) => {
    setForm7(item);
    dispatch(setIsEdited());
  };
  const handleCommitteeChange = (documentCommittees: DocumentCommittee[]) => {
    const f: Form7 = {
      ...form7,
      committeesForDocument: documentCommittees,
    };

    handleUpdate(f);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          !enrollmentDecision &&
          ((item.type !== RequestType.RegularEnrollment &&
            item.type !==
              RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) ||
            item.statusId === RequestStatus.OpinionWithRecommendation) &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.CountyAdministrator ||
            auth.currentRole?.role === Role.CountyLeader ||
            (item.countyCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                ?.some((v) => v.isPresident))) &&
          item.statusId !== RequestStatus.DecisionSuspended
            ? "show"
            : "hide"
        }
        pageTitle={t("form7.form7PageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("form7.form7Data")}>
        <Row>
          <Col md={12}>
            <DatePicker
              label={t("decisionProposal.sessionDate")}
              name={"decisionProposal.sessionDate"}
              onChange={(e) =>
                handleSessionDateChanged(new Date(e.target.value))
              }
              value={form7.sessionDate}
              minYear={new Date().getFullYear()}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionDate"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("form7.sessionPlace")}
              name={nameof<typeof form7>("sessionPlace")}
              value={form7.sessionPlace}
              onChange={(e) => handleSessionPlaceChanged(e.target.value)}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionPlace"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <FormCard cardTitle={t("form7.type")}>
          <Form7TypeSection
            form7={form7}
            item={item}
            errors={errors}
            isEditing={isEditing}
            onChange={handleUpdate}
          />
        </FormCard>
        {form7?.committeesForDocument &&
          initialDocumentCommittees &&
          !!initialDocumentCommittees.length && (
            <CommissionSelection
              committees={form7.committeesForDocument}
              onCommitteeChange={handleCommitteeChange}
              initialDocumentCommittees={initialDocumentCommittees}
              isEditing={isEditing}
            />
          )}
        <Form7InstitutionSelect
          form7={form7}
          item={item}
          isEditing={isEditing}
          errors={errors}
          onInstitutionChange={handleInstitutionChange}
        />
        <FormCard>
          <Row>
            <Col md={6}>
              <Checkbox
                label={t("form7.hasDifficulties")}
                name={nameof<typeof form7>("hasDifficulties")}
                checked={form7.hasDifficulties}
                onChange={(e) => handleHasDifficultiesChange(e.target.checked)}
                plaintext={!isEditing}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.difficultyDescription")}
                name={nameof<typeof form7>("difficultyDescription")}
                value={form7.difficultyDescription}
                onChange={(e) =>
                  handleDifficultyDescriptionChange(e.target.value)
                }
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("DifficultyDescription")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.rules")}
                name={nameof<typeof form7>("rules")}
                value={form7.rules}
                onChange={(e) => handleRulesChange(e.target.value)}
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("Rules"))
                    ?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.nNNumber")}
                name={nameof<typeof form7>("nnNumber")}
                value={form7.nnNumber}
                onChange={(e) => handleNNNumberChange(e.target.value)}
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("NNNumber"))
                    ?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.schoolingDescription")}
                name={nameof<typeof form7>("schoolingDescription")}
                value={form7.schoolingDescription}
                onChange={(e) =>
                  handleSchoolingDescriptionChange(e.target.value)
                }
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("SchoolingDescription")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("form7.notes")}>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.educationalStatus")}
                name={nameof<typeof form7>("educationalStatus")}
                value={form7.educationalStatus}
                onChange={(e) => handleEducationalStatusChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("EducationalStatus")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.appropriateHelpFormat")}
                name={nameof<typeof form7>("appropriateHelpFormat")}
                value={form7.appropriateHelpFormat}
                onChange={(e) =>
                  handleAppropriateHelpFormatChange(e.target.value)
                }
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("AppropriateHelpFormat")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Select
                label={t("form7.difficultyTypes")}
                name={nameof<typeof form7>("difficultyTypes")}
                value={form7.difficultyTypes}
                onChange={(e) => handleDifficultyTypeChange(e.target.value)}
                selectData={codeBookToSelectData(difficultyTypes)}
                errorText={
                  errors?.find((e) => e.propertyName === "DifficultyTypes")
                    ?.message
                }
                disabled={!isEditing}
                multiple
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.other")}
                name={nameof<typeof form7>("other")}
                value={form7.other}
                onChange={(e) => handleOtherChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("Other"))
                    ?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        {!isEditing && form7.attachment && (
          <DocumentList
            documents={form7.attachment ? [form7.attachment] : []}
            onDocumentClick={(doc) =>
              handleDocumentDownload(doc, DocumentType.Form7)
            }
            removable={false}
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={
          isEditing &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.CountyAdministrator ||
            auth.currentRole?.role === Role.CountyLeader ||
            (item.countyCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                ?.some((v) => v.isPresident)))
            ? "show"
            : "hide"
        }
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={async () => saveForm7({ ...form7, request: item })}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
